
import React from 'react';
import mapboxgl from 'mapbox-gl';
import { Church } from '../types';
import { MapPin } from 'lucide-react';
import { useLanguage } from '../../../../contexts/language';

interface ChurchMarkersProps {
  map: mapboxgl.Map;
  churches: Church[];
  selectedChurch: Church | null;
  onChurchSelect: (church: Church) => void;
}

const ChurchMarkers: React.FC<ChurchMarkersProps> = ({
  map,
  churches,
  selectedChurch,
  onChurchSelect,
}) => {
  const { t } = useLanguage();

  React.useEffect(() => {
    const markers: mapboxgl.Marker[] = [];

    churches.forEach(church => {
      if (!church.coordinates) return;

      const el = document.createElement('div');
      el.className = 'church-marker';
      el.style.width = selectedChurch && selectedChurch.id === church.id ? '35px' : '26px';
      el.style.height = selectedChurch && selectedChurch.id === church.id ? '35px' : '26px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#ffffff';
      el.style.display = 'flex';
      el.style.justifyContent = 'center';
      el.style.alignItems = 'center';
      el.style.border = selectedChurch && selectedChurch.id === church.id ? '3px solid #3b82f6' : '2px solid #3b82f6';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)';  // Changed to more solid shadow
      el.style.cursor = 'pointer';
      el.style.transition = 'all 0.2s ease';
      el.style.zIndex = selectedChurch && selectedChurch.id === church.id ? '10' : '1';
      el.style.position = 'absolute'; // Use absolute positioning for stability

      const popup = new mapboxgl.Popup({ 
        offset: 25, 
        className: 'church-popup',
        closeButton: true,
        closeOnClick: true,
        maxWidth: '220px',
        anchor: 'bottom' // Fixed anchor point
      }).setHTML(`
        <div class="p-3 bg-white rounded-lg shadow-lg max-w-[220px]">
          <h3 class="font-bold text-blue-600 pb-1 border-b border-gray-100">${church.name}</h3>
          <p class="text-sm mt-2 text-gray-600">${church.denomination}</p>
          <p class="text-sm text-gray-600">${t('missionaries')}: <span class="font-semibold">${church.missionaries}</span></p>
          <div class="mt-3 pt-2 border-t border-gray-100">
            <button class="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-full hover:bg-blue-600 w-full transition-colors">
              ${t('viewDetails')}
            </button>
          </div>
        </div>
      `);

      // Create marker with fixed positioning options
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom',
        offset: [0, 0],
        pitchAlignment: 'map',
        rotationAlignment: 'map'
      })
        .setLngLat(church.coordinates)
        .setPopup(popup)
        .addTo(map);

      markers.push(marker);

      el.addEventListener('click', () => {
        onChurchSelect(church);
      });
    });

    return () => {
      markers.forEach(marker => marker.remove());
    };
  }, [churches, selectedChurch, t, map, onChurchSelect]);

  return null;
};

export default ChurchMarkers;
